import React from 'react';
import { DEFAULT_FONT_SIZE } from '../signature-request';
import { MIXED_TEXT } from './constants';
import { HyperlinkField, TextField } from './types';
// import getObjectTag from 'common/utils/get-object-tag';

export const mixedSymbol = Symbol('mixed');

// The multiple exports are intentional, it allows TypeScript to know that
// `commonValue(items, prop)` can only return `mixedSymbol`
export function commonValue<T, K extends keyof T, M>(
  items: T[],
  prop: K,
  mixed: M
): null | T[K] | M;
// eslint-disable-next-line no-redeclare
export function commonValue<T, K extends keyof T>(
  items: T[],
  prop: K,
): null | T[K] | typeof mixedSymbol;
// eslint-disable-next-line no-redeclare
export function commonValue<T, K extends keyof T, M>(
  items: T[],
  prop: K,
  mixed: M | typeof mixedSymbol = mixedSymbol,
) {
  if (items.length === 0) {
    // If we don't have any items,the `items[0][prop]` below will throw and we
    // need an initial value when items only holds 1 item
    return null;
  }

  return items.reduce(
    (value, i) => (i[prop] === value ? value : mixed),
    items[0][prop] as T[K] | typeof mixed,
  );
}

export function useHtmlIDs() {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const id = React.useMemo(Math.random, []);
  return (name: string) => `${name}_${id}`;
}

// options: array
export function useMixedOptions<T, K extends keyof T, O = string[]>(
  selectedFields: T[],
  propName: K,
  options: O extends (any[] | readonly any[]) ? O : never,
): [
    typeof MIXED_TEXT | T[K] | null,
    (O | ([typeof MIXED_TEXT] & O))
  ];

// options: object
// eslint-disable-next-line no-redeclare
export function useMixedOptions<T, K extends keyof T, O = { [key: string]: string }>(
  selectedFields: T[],
  propName: K,
  options: O,
): [
    typeof MIXED_TEXT | T[K] | null,
    (O | { mixed: typeof MIXED_TEXT } & O)
  ];

// eslint-disable-next-line no-redeclare
export function useMixedOptions<T, K extends keyof T, O>(
  selectedFields: T[],
  propName: K,
  options: O,
) {
  const value = commonValue(
    selectedFields,
    propName,
    MIXED_TEXT,
  );

  if (value !== MIXED_TEXT) {
    return [value, options];
  }

  let availableOptions;

  if (Array.isArray(options)) {
    availableOptions = [MIXED_TEXT, ...options];
  } else {
    availableOptions = Object.assign({
      mixed: MIXED_TEXT,
    }, options);
  }

  return [value, availableOptions];
}

export function isMultilineField(field: TextField | HyperlinkField): boolean {
  return field.height >= (field.fontSize || DEFAULT_FONT_SIZE) * 2;
}
